import { Disclosure } from '@headlessui/react';
import { Fragment, useMemo } from 'react';
import type { JSX } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FiltersBar from '@/components/FiltersBar';
import { Color } from '@/constants/color';
import ActiveFilters from '@/forms/TimelineFilterForm/ActiveFilters';
import { ACTION_FILTERS, Props, TimelineFilters } from '@/forms/TimelineFilterForm/types';
import useUniqueActivityLogEvents from '@/hooks/useUniqueActivityLogEvents';
import Accordion from '@/ui/components/Accordion/Accordion';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button/Button';
import DatetimePicker from '@/ui/components/Form/DatetimePicker';
import InputField from '@/ui/components/Form/InputField/InputField';
import Label from '@/ui/components/Form/Label';
import Spinner from '@/ui/components/Spinner';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

const TimelineFilterForm = ({ activeFilters, setFilters }: Props): JSX.Element => {
  const { data: uniqueEventsResponse, isPending: isPendingUniqueEvents } =
    useUniqueActivityLogEvents();

  const noteEvents = useMemo(() => {
    return (
      uniqueEventsResponse?.filter((action) => action && action.includes('_note_created')) ?? []
    );
  }, [uniqueEventsResponse]);

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch
  } = useForm({
    defaultValues: activeFilters,
    values: activeFilters
  });
  const values = watch();

  const applyFilters = (filterValues: Partial<TimelineFilters>): void => {
    setFilters(filterValues);
  };

  const ApplyFiltersButton = () => (
    <Disclosure.Button as="div">
      <Button className="mt-1" color={Color.INDIGO} disabled={isSubmitting} size="xs" type="submit">
        {isSubmitting ? <Spinner /> : 'Apply'}
      </Button>
    </Disclosure.Button>
  );

  const handleNoteFilterSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const noteEventsActions = noteEvents.map((action) => action);

    // if isChecked, add all noteEventsActions to the form values action
    if (isChecked) {
      setValue(
        'action',
        values.action ? [...values.action, ...noteEventsActions] : noteEventsActions
      );
    } else {
      // if not isChecked, remove all noteEventsActions from the form values action
      setValue(
        'action',
        values.action?.filter((action) => !noteEventsActions.includes(action))
      );
    }
  };

  const renderFilterSets = () => {
    return (
      <>
        <div className="mb-1">
          <Accordion buttonText="Event">
            {errors.action?.message && <Alert level="error" message={errors.action.message} />}
            {Object.values(ACTION_FILTERS).map((filter) => {
              const registerAction = register('action');

              if (filter === ACTION_FILTERS.NOTE) {
                return (
                  <Fragment key="action-note">
                    <Label
                      className="flex items-center text-underline"
                      htmlFor="action-note"
                      reverse
                      text="Note (select all)">
                      <input
                        className="mr-1"
                        checked={noteEvents.every((action) => values.action?.includes(action))}
                        id="action-note"
                        onChange={handleNoteFilterSelectAll}
                        type="checkbox"
                        value={filter}
                      />
                    </Label>

                    {isPendingUniqueEvents && <Spinner />}

                    <div className="ml-4">
                      {noteEvents.map((action) => {
                        const id = `action-${action}`;

                        return (
                          <Label
                            className="flex items-center"
                            key={action}
                            htmlFor={id}
                            reverse
                            text={action ? snakeCaseToTitleCase(action) : ''}>
                            <input
                              {...registerAction}
                              className="mr-1"
                              id={id}
                              type="checkbox"
                              value={action ?? ''}
                            />
                          </Label>
                        );
                      })}
                    </div>
                  </Fragment>
                );
              }

              const id = `action-${filter}`;

              return (
                <Label className="flex items-center" key={id} htmlFor={id} reverse text={filter}>
                  <input
                    {...registerAction}
                    className="mr-1"
                    id={id}
                    type="checkbox"
                    value={filter}
                  />
                </Label>
              );
            })}
            <ApplyFiltersButton />
          </Accordion>
        </div>

        <div className="mb-1">
          <Accordion buttonText="Date range">
            <div className="mb-3">
              {errors.created_at__gte?.message && (
                <Alert level="error" message={errors.created_at__gte.message} />
              )}
              <Controller
                name="created_at__gte"
                control={control}
                render={({ field }) => {
                  return (
                    <DatetimePicker
                      onBlur={field.onBlur}
                      onChange={(val) => field.onChange(val ? val.toISOString() : val)}
                      label="From"
                      name={field.name}
                      showTimeSelect={false}
                      value={field.value}
                    />
                  );
                }}
              />
            </div>

            {errors.created_at__lte?.message && (
              <Alert level="error" message={errors.created_at__lte.message} />
            )}
            <Controller
              name="created_at__lte"
              control={control}
              render={({ field }) => {
                return (
                  <DatetimePicker
                    onBlur={field.onBlur}
                    onChange={(val) => field.onChange(val ? val.toISOString() : val)}
                    label="To"
                    name={field.name}
                    showTimeSelect={false}
                    value={field.value}
                  />
                );
              }}
            />
            <ApplyFiltersButton />
          </Accordion>
        </div>
      </>
    );
  };

  return (
    <form className="relative" onSubmit={handleSubmit(applyFilters)}>
      <FiltersBar
        filterSets={renderFilterSets()}
        search={
          <Controller
            name="search"
            control={control}
            render={({ field }) => {
              return (
                <InputField
                  {...field}
                  id="search"
                  placeholder="Search"
                  type="text"
                  typeHelper="search"
                />
              );
            }}
          />
        }>
        {!!Object.entries(activeFilters).length && (
          <ActiveFilters filters={activeFilters} setFilters={setFilters} values={values} />
        )}
      </FiltersBar>
    </form>
  );
};

export default TimelineFilterForm;
